import { useState } from 'react'
import type { KpiValueType } from '../lib/kpiApi'

interface Props {
  valueType: KpiValueType
  /** What is already stored for this period, if anything — seeds the input. */
  initialValue: number | null
  /** Placeholder for the number input. Ignored for a yes/no metric. */
  placeholder: string
  /** Group label for the Yes/No pair, e.g. "Enter this week's answer". */
  ariaLabel: string
  busy: boolean
  /** A failure from the server, shown above the actions. */
  problem: string | null
  onCancel: () => void
  onSave: (value: number) => void
}

/**
 * The one control for entering a KPI number — a number input with Save, or a
 * Yes/No pair that saves on tap. Used by the card on My Portal and by the grid
 * on the Scorecard tab, so the two cannot drift on what "enter a number" means.
 *
 * Owns its own draft: it is only mounted while an entry is open, so unmounting
 * on Cancel is what resets it.
 */
export default function KpiEntry({
  valueType,
  initialValue,
  placeholder,
  ariaLabel,
  busy,
  problem,
  onCancel,
  onSave,
}: Props) {
  const [draft, setDraft] = useState(initialValue != null ? String(initialValue) : '')
  const [localProblem, setLocalProblem] = useState<string | null>(null)

  const isBoolean = valueType === 'boolean'

  const saveNumber = () => {
    const parsed = Number(draft)
    if (draft.trim() === '' || Number.isNaN(parsed)) {
      setLocalProblem('Enter a number.')
      return
    }
    setLocalProblem(null)
    onSave(parsed)
  }

  const shown = problem ?? localProblem

  return (
    <div className="note kpi-entry">
      {isBoolean ? (
        <div className="seg seg--2" role="group" aria-label={ariaLabel}>
          <button
            type="button"
            className={'seg__btn' + (draft === '1' ? ' seg__btn--on' : '')}
            disabled={busy}
            onClick={() => {
              setDraft('1')
              onSave(1)
            }}
          >
            Yes
          </button>
          <button
            type="button"
            className={'seg__btn' + (draft === '0' ? ' seg__btn--on' : '')}
            disabled={busy}
            onClick={() => {
              setDraft('0')
              onSave(0)
            }}
          >
            No
          </button>
        </div>
      ) : (
        <input
          className="field__input"
          type="number"
          inputMode="decimal"
          step="any"
          autoFocus
          value={draft}
          placeholder={placeholder}
          aria-label={ariaLabel}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') saveNumber()
          }}
        />
      )}
      {shown && <p className="banner banner--tight">{shown}</p>}
      <div className="note__actions">
        <button type="button" className="btn btn--quiet" onClick={onCancel}>
          Cancel
        </button>
        {!isBoolean && (
          <button type="button" className="btn" disabled={busy} onClick={saveNumber}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        )}
      </div>
    </div>
  )
}
